import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import {
  getApiErrorUserMessage,
  readApiError,
  type ApiErrorReaderOptions,
} from "_shared/_errors/api-error";

type ApiErrorMessageOptions = ApiErrorReaderOptions & {
  includeFallbackStrings?: boolean;
  prioritizedFields?: readonly string[];
};

const fetchBaseQueryStringStatuses = ["FETCH_ERROR", "PARSING_ERROR", "TIMEOUT_ERROR", "CUSTOM_ERROR"] as const;

export function isFetchBaseQueryError(error: unknown): error is FetchBaseQueryError {
  return typeof error === "object" && error !== null && "status" in error;
}

export function getApiErrorStatus(error: unknown): FetchBaseQueryError["status"] | undefined {
  if (!isRecord(error) || !("status" in error)) return undefined;
  const status = error.status;
  return isApiErrorStatus(status) ? status : undefined;
}

export function getApiErrorData(error: unknown): unknown {
  return isRecord(error) && "data" in error ? error.data : undefined;
}

/**
 * TODO(#4907): Remove this compatibility facade and migrate its callers to the typed error reader
 * after #4907 and every linked API migration subtask emit only the v1 Problem Details profile.
 * Only v1 Problem Details or an exact module-owned legacy mapping can produce text; arbitrary
 * legacy strings are deliberately not returned.
 */
export function getApiErrorMessage(data: unknown, options: ApiErrorMessageOptions = {}): string | undefined {
  return getApiErrorUserMessage(readApiError(data, options));
}

export function getErrorMessage(error: unknown): string | undefined {
  if (error instanceof Error && error.message.trim()) return error.message;
  return undefined;
}

export function getErrorMessageFromUnknown(
  error: unknown,
  options: ApiErrorReaderOptions = {},
): string | undefined {
  if (error instanceof Error && !isFetchBaseQueryError(error)) return error.message.trim() || undefined;
  if (isFetchBaseQueryError(error) || (isRecord(error) && "attorneyHubError" in error)) {
    return getApiErrorUserMessage(readApiError(error, options));
  }
  return undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isApiErrorStatus(value: unknown): value is FetchBaseQueryError["status"] {
  return typeof value === "number" || fetchBaseQueryStringStatuses.some((status) => status === value);
}
